/**
 * Account → Abonnement & facturen (accountbeheerder van de tenant) — Stripe Checkout.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { CreditCard } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

function eur(cents: number) {
  return `€ ${(cents / 100).toFixed(2)}`;
}

export function BillingPortal() {
  const { user, refreshUser } = useAuth();
  const tenantId = user?.tenant_id ?? 'default';
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkoutFlash, setCheckoutFlash] = useState<string | null>(null);
  const [syncBanner, setSyncBanner] = useState<string | null>(null);

  useEffect(() => {
    const c = searchParams.get('checkout');
    if (c === 'success') {
      setCheckoutFlash('Betaling geslaagd. Je abonnement wordt zo bijgewerkt (even wachten op bevestiging).');
      void qc.invalidateQueries({ queryKey: ['billing-portal', tenantId] });
      void refreshUser();
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      setSearchParams(next, { replace: true });
    } else if (c === 'cancel') {
      setCheckoutFlash('Checkout geannuleerd. Je kunt opnieuw proberen wanneer je wilt.');
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, qc, tenantId, refreshUser]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['billing-portal', tenantId],
    queryFn: () => api.billingPortal(tenantId),
    enabled: !!user?.authenticated,
  });

  const incompleteSub = data?.subscriptions?.find((s) => s.status === 'incomplete');

  useEffect(() => {
    if (data?.billing_sync_recovered) {
      setSyncBanner(
        'Facturatie is automatisch bijgewerkt (bijv. oude test-checkout of ontbrekende Stripe-klant opgeruimd). Je kunt opnieuw proberen af te rekenen.'
      );
    }
  }, [data?.billing_sync_recovered]);

  const [planId, setPlanId] = useState<'basis' | 'plus' | 'pro'>('plus');
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [legalName, setLegalName] = useState('');
  const [checkoutResult, setCheckoutResult] = useState<{
    invoice_number: string;
    total_cents: number;
    checkout_url?: string;
  } | null>(null);

  const activeSub = (data?.subscriptions ?? []).find((s) =>
    ['active', 'trialing', 'past_due'].includes(s.status)
  );

  const changePlanMut = useMutation({
    mutationFn: () =>
      api.billingChangePlan({
        plan_id: planId,
        interval,
        tenant_id: tenantId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-portal', tenantId] });
      void refreshUser();
    },
  });

  const checkoutMut = useMutation({
    mutationFn: () =>
      api.billingCheckout({
        plan_id: planId,
        interval,
        method: 'stripe',
        legal_name: legalName || undefined,
        tenant_id: tenantId,
      }),
    onSuccess: (res) => {
      if (res.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }
      setCheckoutResult({
        invoice_number: res.invoice_number,
        total_cents: res.total_cents,
        checkout_url: res.checkout_url,
      });
      void qc.invalidateQueries({ queryKey: ['billing-portal', tenantId] });
      void refreshUser();
    },
  });

  const stripePortalMut = useMutation({
    mutationFn: () => api.billingStripePortal({ tenant_id: tenantId }),
    onSuccess: (res) => {
      window.location.assign(res.url);
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: ['billing-portal', tenantId] });
      void refreshUser();
    },
  });

  const abandonMut = useMutation({
    mutationFn: () => api.billingAbandonCheckout({ tenant_id: tenantId }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['billing-portal', tenantId] });
      void refreshUser();
      setSyncBanner(
        res.removed > 0
          ? 'Open checkout is opgegeven. Je kunt opnieuw afrekenen met Stripe.'
          : 'Er stond geen open checkout meer in CalqTool.'
      );
    },
  });

  const errMsg = error instanceof Error ? error.message : String(error ?? '');

  return (
    <div>
      <SeoMeta
        path="/account/billing"
        noindex
        title="Abonnement & facturatie"
        description="CalqTool™ abonnement en facturatie via Stripe: betalen met iDEAL of kaart, facturen bekijken en klantportaal — voor tenant-accountbeheerders."
      />
      <Breadcrumbs items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Facturatie' }]} />
      <div className="mb-6 flex items-center gap-2">
        <CreditCard className="h-6 w-6 text-primary" aria-hidden />
        <h1 className="text-2xl font-bold text-text">Abonnement & facturatie</h1>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-text-muted">
        Nieuwe abonnementen worden afgerekend via <strong className="text-text">Stripe</strong> (iDEAL, kaart, enz.).
        Facturen en betalingshistoriek kun je ook in het Stripe-klantportaal bekijken zodra je een checkout hebt
        voltooid. Na het klantportaal kom je doorgaans weer terug op deze pagina. Stripe kan je e-mailen over betalingen
        (o.a. mislukte incasso of als je bank extra bevestiging vraagt; herinneringen daarvoor o.a. op{' '}
        <strong className="text-text">1, 3 en 5 dagen</strong>). Zie{' '}
        <Link to="/terms" className="font-medium text-primary hover:underline">
          voorwaarden
        </Link>{' '}
        en{' '}
        <Link to="/privacy" className="font-medium text-primary hover:underline">
          privacy
        </Link>
        .
      </p>
      <p className="mb-6 max-w-2xl rounded-lg border border-border/80 bg-surface/60 p-4 text-sm text-text-muted">
        <strong className="text-text">Accountbeheerder</strong> is de rol die organisatie en betaling regelt (meestal de
        eerste registrant). <strong className="text-text">Pakket Plus of Pro</strong> staat meer gelijktijdige apparaten
        per gebruiker toe dan Basis — handig voor laptop én telefoon. Zie ook{' '}
        <Link to="/account#account-sessies" className="font-medium text-primary hover:underline">
          Account → Inloggen &amp; apparaten
        </Link>{' '}
        en{' '}
        <Link to="/prijzen" className="font-medium text-primary hover:underline">
          Pakketten
        </Link>
        .
      </p>

      {checkoutFlash && (
        <p
          className="mb-4 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-text"
          role="status"
        >
          {checkoutFlash}
        </p>
      )}

      {syncBanner && (
        <p
          className="mb-4 rounded-lg border border-amber-200/90 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-50"
          role="status"
        >
          {syncBanner}{' '}
          <button
            type="button"
            className="ml-1 font-medium text-primary underline"
            onClick={() => setSyncBanner(null)}
          >
            Sluiten
          </button>
        </p>
      )}

      {isLoading && <p className="text-text-muted">Laden…</p>}
      {error && (
        <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200" role="alert">
          {errMsg}
        </p>
      )}

      {data && (
        <div className="space-y-8">
          {incompleteSub && (
            <section
              className="rounded-xl border border-amber-200/90 bg-amber-50 p-5 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-50"
              role="region"
              aria-label="Open checkout"
            >
              <h2 className="mb-2 font-semibold text-text">Open checkout in CalqTool</h2>
              <p className="mb-3 text-text-muted">
                Er staat een <strong className="text-text">incomplete</strong> abonnementsregistratie (bijv. checkout
                gestopt of Stripe anders opgeschoond). Daardoor kun je soms geen nieuwe checkout starten. Als je in Stripe
                alles hebt verwijderd of dit was een test, kun je de lokale registratie opgeven — daarna start je opnieuw
                met &quot;Afrekenen met Stripe&quot;.
              </p>
              {abandonMut.isError && (
                <p className="mb-2 text-sm text-red-700 dark:text-red-200">
                  {abandonMut.error instanceof Error ? abandonMut.error.message : 'Opgeven mislukt'}
                </p>
              )}
              <button
                type="button"
                disabled={abandonMut.isPending}
                onClick={() => abandonMut.mutate()}
                className="min-h-[44px] rounded-lg border border-amber-800/30 bg-surface px-4 py-2 text-sm font-medium text-text hover:bg-amber-100/80 disabled:opacity-50 dark:border-amber-200/20 dark:hover:bg-amber-900/40"
              >
                {abandonMut.isPending ? 'Bezig…' : 'Open checkout opgeven (lokaal resetten)'}
              </button>
            </section>
          )}

          {data.stripe_customer_id && (
            <section className="rounded-xl border border-border bg-surface-card p-6 shadow-sm">
              <h2 className="mb-2 text-lg font-semibold text-text">Stripe-klantportaal</h2>
              <p className="mb-4 text-sm text-text-muted">
                Facturen downloaden, betaalmethode wijzigen of opzeggen via Stripe.
              </p>
              {stripePortalMut.isError && (
                <p className="mb-2 text-sm text-red-600">
                  {stripePortalMut.error instanceof Error ? stripePortalMut.error.message : 'Portaal mislukt'}
                </p>
              )}
              <button
                type="button"
                disabled={stripePortalMut.isPending}
                onClick={() => stripePortalMut.mutate()}
                className="min-h-[44px] rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text hover:bg-surface-card disabled:opacity-50"
              >
                {stripePortalMut.isPending ? 'Bezig…' : 'Open Stripe-portaal'}
              </button>
            </section>
          )}

          {activeSub && (
            <section className="rounded-xl border border-primary/25 bg-primary/5 p-6 shadow-sm">
              <h2 className="mb-2 text-lg font-semibold text-text">Plan wijzigen (actief abonnement)</h2>
              <p className="mb-4 text-sm text-text-muted">
                Je hebt een abonnement met status <strong className="text-text">{activeSub.status}</strong>. Kies hier een
                ander plan of interval; wijzigingen lopen via Stripe met evenredige (pro rata) verrekening waar van
                toepassing, zoals in de{' '}
                <Link to="/terms" className="font-medium text-primary hover:underline">
                  voorwaarden
                </Link>{' '}
                beschreven.
              </p>
              <div className="grid max-w-xl gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-text">Nieuw plan</label>
                  <select
                    value={planId}
                    onChange={(e) => setPlanId(e.target.value as typeof planId)}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
                  >
                    {(['basis', 'plus', 'pro'] as const).map((id) => (
                      <option key={id} value={id}>
                        {data.plans[id]?.label ?? id}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-text">Interval</label>
                  <select
                    value={interval}
                    onChange={(e) => setInterval(e.target.value as 'month' | 'year')}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
                  >
                    <option value="month">Maand</option>
                    <option value="year">Jaar</option>
                  </select>
                </div>
              </div>
              {changePlanMut.isError && (
                <p className="mt-3 text-sm text-red-600">
                  {changePlanMut.error instanceof Error ? changePlanMut.error.message : 'Wijzigen mislukt'}
                </p>
              )}
              {changePlanMut.isSuccess && (
                <p className="mt-3 text-sm text-text" role="status">
                  Plan bijgewerkt.
                </p>
              )}
              <button
                type="button"
                disabled={changePlanMut.isPending}
                onClick={() => changePlanMut.mutate()}
                className="mt-4 min-h-[44px] rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {changePlanMut.isPending ? 'Bezig…' : 'Abonnement bijwerken'}
              </button>
            </section>
          )}

          <section className="rounded-xl border border-border bg-surface-card p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-text">Nieuwe checkout</h2>
            <div className="grid max-w-xl gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-text">Plan</label>
                <select
                  value={planId}
                  onChange={(e) => setPlanId(e.target.value as typeof planId)}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
                >
                  {(['basis', 'plus', 'pro'] as const).map((id) => (
                    <option key={id} value={id}>
                      {data.plans[id]?.label ?? id}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text">Interval</label>
                <select
                  value={interval}
                  onChange={(e) => setInterval(e.target.value as 'month' | 'year')}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
                >
                  <option value="month">Maand</option>
                  <option value="year">Jaar</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text">Factuurnaam (optioneel)</label>
                <input
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
                  placeholder="Bedrijfsnaam"
                />
              </div>
              {checkoutMut.isError && (
                <p className="text-sm text-red-600">
                  {checkoutMut.error instanceof Error ? checkoutMut.error.message : 'Checkout mislukt'}
                </p>
              )}
              <button
                type="button"
                disabled={checkoutMut.isPending}
                onClick={() => checkoutMut.mutate()}
                className="min-h-[44px] rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {checkoutMut.isPending ? 'Bezig…' : 'Afrekenen met Stripe'}
              </button>
            </div>

            {checkoutResult && (
              <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
                <p className="font-medium text-text">
                  Factuur {checkoutResult.invoice_number} — {eur(checkoutResult.total_cents)} incl. btw
                </p>
                {!checkoutResult.checkout_url && (
                  <p className="mt-2 text-text-muted">
                    Geen checkout-URL ontvangen. Controleer Worker-configuratie (Stripe, PUBLIC_APP_URL).
                  </p>
                )}
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-text">Abonnementen</h2>
            <ul className="space-y-2 text-sm">
              {(data.subscriptions ?? []).length === 0 && <li className="text-text-muted">Geen abonnementen.</li>}
              {(data.subscriptions ?? []).map((s) => (
                <li key={s.subscription_id} className="rounded border border-border bg-surface-card px-3 py-2">
                  <span className="font-medium">{s.plan_id}</span> · {s.interval} ·{' '}
                  <span className="text-text-muted">{s.status}</span>
                  {s.current_period_end && (
                    <span className="ml-2 text-text-muted">tot {new Date(s.current_period_end).toLocaleDateString('nl-NL')}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-text">Facturen</h2>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[480px] text-left text-sm">
                <thead className="bg-surface">
                  <tr>
                    <th className="p-2">Nummer</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Bedrag</th>
                    <th className="p-2">Vervaldatum</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.invoices ?? []).length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-3 text-text-muted">
                        Geen facturen.
                      </td>
                    </tr>
                  )}
                  {(data.invoices ?? []).map((inv) => (
                    <tr key={inv.invoice_id} className="border-t border-border">
                      <td className="p-2 font-mono text-xs">{inv.invoice_number}</td>
                      <td className="p-2">{inv.status}</td>
                      <td className="p-2">{eur(inv.total_cents)}</td>
                      <td className="p-2 text-text-muted">{new Date(inv.due_at).toLocaleDateString('nl-NL')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
